// ── Resources ("/resources") — the resource library (Wave 8) ────────────────
// ONE honest read (the server-scope-filtered resource list) routed through
// useHonestQuery → <HonestState>. The page NEVER hand-rolls state branching: a
// gate / permission-denied / unavailable / error response renders via
// <HonestState> (tagged data-honest-posture), NEVER as a plain empty table.
//
// ── PARTICIPANT EXCLUSION (Wave 8 release gate) ──────────────────────────────
// A `participant` is DENIED the ADMIN module entirely:
//   - the route manifest's resources route is generic-not-found for a direct URL
//     + has no nav for participant at the ROUTE level (resourceModuleRoles);
//   - THIS page ALSO fail-closes (canAccessResourcesModule) so a participant who
//     somehow reached the component renders ONLY permission-denied with NO list —
//     and CRITICALLY the resource read NEVER fires for a participant (`enabled`
//     gates the query). The participant's resources context is the Wave-9 dash.
//
// ── manage gating ────────────────────────────────────────────────────────────
// Create/edit/archive (manage.resources) is STAFF-ONLY (canManageResources). The
// non-staff roles (coach / company_admin / cohort_lead) get a scoped VIEW with no
// manage affordances. Coach can VIEW reference videos but NOT download them — the
// download restriction lives on the detail surface.

import { Button, CollectionToolbar, IconDoc, PageFrame, ProTable, useToast } from "@vjal/ui";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { focusedListOrgQuery, focusedListQueryKey } from "../../hooks/focused-route-prefetch";
import { useArtifactWorkflowMutations } from "../../hooks/useArtifactWorkflowMutations";
import type {  CoachingResourceDto, ListResponse } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useResourcesCollection } from "./resources-collection";
import { canAccessResourcesModule, canManageResources } from "./resources-helpers";
import { ResourceFormModal, type ResourceFormValues , resourceWriteBody } from "./resource-form-modal";

const services = getCoachingServices();

export function ResourcesPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const moduleDenied = !canAccessResourcesModule(role);
  const canManage = canManageResources(role);

  const [modalOpen, setModalOpen] = useState(false);
  const { createResource } = useArtifactWorkflowMutations();
  const listQuery = focusedListOrgQuery();

  const query = useHonestQuery<ListResponse<CoachingResourceDto>>(
    focusedListQueryKey("resources.list", listQuery?.orgId ?? null),
    () => services.resources.list(listQuery),
    { enabled: !moduleDenied }
  );

  const items = query.data?.items;
  const allRows = useMemo(() => items ?? [], [items]);
  const collection = useResourcesCollection(allRows, routePath);

  // ── Fail closed: a denied role gets a permission-denied posture, NO read. ───
  if (moduleDenied) {
    return (
      <section aria-label="Resources" className="col-span-12" data-testid="resources-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to the resources admin"
            message="The resources admin is for coaching staff and assigned leads. Resources shared with you appear on your dashboard."
          />
        </div>
      </section>
    );
  }

  const filteredToEmpty = query.posture === null && collection.total === 0;
  const posture = filteredToEmpty ? "empty" : query.posture;
  const gated = posture !== null;
  const showToolbar = query.posture === null || posture === "empty";

  function submitCreate(values: ResourceFormValues) {
    return createResource.mutateAsync(resourceWriteBody(values), {
      onSuccess: () => {
        toast.success("Resource created");
        setModalOpen(false);
      }
    });
  }

  return (
    <PageFrame title="Resources" subtitle="Learning resources published in your scope." className="col-span-12">
      <section aria-label="Resources" className="flex flex-col gap-4" data-testid="resources-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by title or description…"
            primaryAction={
              canManage ? (
                <Button type="button" leftIcon={<Plus size={16} />} onClick={() => setModalOpen(true)} data-testid="resource-create">
                  Add resource
                </Button>
              ) : undefined
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={query.gate}
              reference={query.reference}
              onRetry={query.retry}
              icon={<IconDoc size={22} />}
              title={posture === "empty" ? "No resources yet" : undefined}
              message={
                posture === "empty"
                  ? "Resources you're authorized to see will appear here once they're published."
                  : undefined
              }
              canCreate={posture === "empty" && canManage}
              createAction={
                <Button type="button" leftIcon={<Plus size={16} />} onClick={() => setModalOpen(true)}>
                  Add the first resource
                </Button>
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={collection}
              primaryAction={
                canManage ? (
                  <Button type="button" leftIcon={<Plus size={16} />} onClick={() => setModalOpen(true)} data-testid="resource-create">
                    Add resource
                  </Button>
                ) : undefined
              }
              className="p-0"
              tableProps={{
                caption: "Resources in the current view",
                emptyTitle: "No resources match your search",
                emptyMessage: "Clear or change the filters above to widen the view.",
              }}
              stableChipNames
            />
          )}
        </div>

      {canManage ? (
        <ResourceFormModal
          open={modalOpen}
          mode="create"
          onClose={() => setModalOpen(false)}
          onSubmit={submitCreate}
          submitting={createResource.isPending}
          submitError={createResource.error}
        />
      ) : null}
      </section>
    </PageFrame>
  );
}
